import AlertPrompt from "components/AlertPrompt"
import { createModal } from "components/Modals/ModalFactory"
import { ModalWrapper } from "components/Modals/ModalWrapper"
import { ModalDataAttributes } from "components/Modals/types"
import Translation from "components/Translation"
import { useAppContext } from "context/AppContext"
import { useAppErrorApiContext } from "context/AppErrorContext"
import { AuthContext, useAuth } from "context/AuthContext"
import { useTenantContext } from "context/TenantContext"
import { useEffect, useState } from "react"
import { OrderList, mapToOrderDTO } from "types/order"
import { Commission } from "types/reports/sales-and-commission"
import { classNames } from "utils/dom-helpers"
import { putApiRequest } from "utils/fetch-helper"
import { getTranslation } from "utils/language-helper"

const ModalProvider = createModal(
    AuthContext,
    'OrderEditModal',
    () => (
        <>
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-x " />
        </span>
    )
)

export const OrderEditModalOpener = ModalProvider.Opener
export const OrderEditModalCloser = ModalProvider.Closer

export function OrderEditModal() {
    const { OrderEditModal, CreateClientModal } = useAuth()
    const { attributes, setAttributes } = OrderEditModal
    const {
        attributes: client_modal_attributes,
        setAttributes: setClientModalAttributes
    } = CreateClientModal
    const { staff } = useTenantContext()
    const { setGlobalError } = useAppErrorApiContext()
    const { setGlobalLoading, translations } = useAppContext()
    const [selected_staff, setSelectedStaff] = useState<string | undefined>(undefined)
    const [commission_amount, setCommissionAmount] = useState<number>(0)
    const [is_valid_amount, toggleIsValidAmount] = useState<boolean>(true)
    const [show_success, toggleShowSuccess] = useState<boolean>(false)

    function handleClose() {
        setAttributes({})
        OrderEditModal.close()
    }

    async function handleSubmit() {
        try {
            if (!attributes?.order) return
            setGlobalLoading(true)
            const order = attributes.order as OrderList
            const payload = mapToOrderDTO({
                ...(attributes.order as OrderList),
                commission: Commission.CUSTOM,
                commission_amount: commission_amount,
                sold_by: selected_staff || ""
            })
            let res = await putApiRequest(`/v1/orders/${order.id}`, payload)
            if (res?.ok) {
                toggleShowSuccess(true)
            }
        } catch (err) {
            setGlobalError(err)
        } finally {
            setGlobalLoading(false)
        }
    }

    useEffect(() => {
        if (attributes?.order) {
            setSelectedStaff((attributes.order as ModalDataAttributes)?.sold_by as string)
            setCommissionAmount((attributes.order as ModalDataAttributes)?.commission_amount as number || 0)
        } else {
            setSelectedStaff(undefined)
            toggleIsValidAmount(true)
            setCommissionAmount(0)
        }
    }, [attributes?.order])

    useEffect(() => {
        if (attributes?.order) {
            toggleIsValidAmount(commission_amount < ((attributes?.order as ModalDataAttributes)?.total as number))
        }
    }, [commission_amount])

    return (
        <ModalProvider.Visible>
            <ModalWrapper className="w-auto">
                <>
                    {!show_success &&
                        <div className="bg-white shadow-2xl overflow-x-hidden sm:rounded-md relative border-2 border-gray-100 my-auto">
                            <div className="flex justify-between text-lg text-primary-dark px-4 text-gray-500 z-10 w-full pt-4 bg-opacity-30 pb-3 bg-primary-lighter">
                                <Translation
                                    render_as="span"
                                    className="circular ml-3"
                                    content_key="clients.edit_order"
                                />
                            </div>
                            <div className="flex flex-col w-full p-10 text-black">
                                <div className="flex flex-col lg:flex-row">
                                    <fieldset className="pb-6 w-2/3 lg:pr-8">
                                        <label
                                            htmlFor="clients.edit_sales_staff"
                                            className="block text-lg truncate"
                                        >
                                            <Translation content_key="clients.edit_sales_staff" />
                                        </label>
                                        <select
                                            className="px-6 py-3 mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md"
                                            onChange={(e) => setSelectedStaff(e.target.value)}
                                            value={selected_staff}
                                        >
                                            <option value={undefined}></option>
                                            {staff?.map((item) => (
                                                <option key={item?.id} value={item?.id}>
                                                    {`${item?.user?.first_name} ${item?.user?.last_name}`}
                                                </option>
                                            ))}
                                        </select>

                                    </fieldset>
                                    <fieldset className="pb-6 w-1/3">
                                        <label
                                            htmlFor="clients.edit_commission_amount"
                                            className="block text-lg truncate"
                                        >
                                            <Translation content_key="clients.edit_commission_amount" />
                                        </label>
                                        <input
                                            type="number"
                                            id="clients.edit_commission_amount"
                                            className={classNames("px-6 py-3 mt-1 block w-full shadow-sm rounded-md",
                                                is_valid_amount
                                                    ? "border-gray-300 focus:ring-primary-light focus:border-primary-light"
                                                    : "border-red-500 focus:ring-transparent")}
                                            value={commission_amount.toString()}
                                            style={{
                                                border: "1px solid gray"
                                            }}
                                            onChange={(e) =>
                                                setCommissionAmount(+e.target.value)
                                            }
                                            title={is_valid_amount ? '' : getTranslation("order.error.commission_exceeds_total_amount", translations)}
                                        />

                                    </fieldset>
                                </div>
                                <Translation render_as="small" className={classNames("text-red-500", is_valid_amount ? "invisible" : "visible")} content_key="order.error.commission_exceeds_total_amount" />
                                <div>
                                    <div className="flex justify-end gap-3 mt-10">
                                        <button
                                            type="reset"
                                            onClick={handleClose}
                                            className="border-gray-200 rounded-lg border px-10"
                                        >
                                            <Translation content_key="cancel" />
                                        </button>
                                        <button
                                            type="submit"
                                            onClick={handleSubmit}
                                            className={classNames(
                                                'group relative flex justify-center',
                                                'py-3 px-12 border border-transparent',
                                                'rounded-md text-white bg-primary',
                                                is_valid_amount
                                                    ? "hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light focus:outline-none"
                                                    : "opacity-50 cursor-not-allowed"
                                            )}
                                            disabled={!is_valid_amount}
                                        >
                                            <Translation content_key="save" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </>
                <AlertPrompt
                    show={show_success}
                    title={"clients.successfully_updated_order"}
                    className="z-[101] my-auto"
                    actions={[
                        <button
                            key={"success_ok"}
                            className="px-7 py-2 text-white bg-primary rounded-md w-full"
                            onClick={() => {
                                toggleShowSuccess(false)
                                setClientModalAttributes({
                                    ...client_modal_attributes,
                                    refetch_orders: true
                                })
                                handleClose()
                            }}
                        >
                            <Translation content_key="ok" />
                        </button>
                    ]}
                />
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}

export default OrderEditModal
